import React, { useMemo, useState } from "react";

/**
 * 3D SKELETON view - isometric WIREFRAME (edges only, NO surfaces) so the
 * structure/"عظم" of the building reads clearly: walls as extruded planes,
 * columns as vertical prisms, one floor stacked per storey.
 * Pure SVG (no WebGL) → light, deterministic, works on every browser.
 */
const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;

const iso = (x, y, z) => ({ x: (x - y) * COS30, y: (x + y) * SIN30 - z });

export const Skeleton3D = ({ model, floors = 1, wallHeight = 3, className = "" }) => {
  const [spin, setSpin] = useState(0); // 0 / 90 / 180 / 270 - rotate footprint
  const data = useMemo(() => {
    if (!model?.walls?.length) return null;
    const H = wallHeight || 3;
    const nFloors = Math.max(1, Math.min(floors || 1, 8));
    const ew = model.extent_w || 20;
    const eh = model.extent_h || 20;
    const rot = (px, py) => {
      // rotate footprint around its centre by `spin` degrees
      const cx = ew / 2, cy = eh / 2;
      const dx = px - cx, dy = py - cy;
      const a = (spin * Math.PI) / 180;
      return { x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a) };
    };
    const segs = []; // { a:{x,y}, b:{x,y}, cls }
    const pt = (x, y, z) => { const r = rot(x, y); return iso(r.x, r.y, z); };
    const line = (x1, y1, z1, x2, y2, z2, cls) => segs.push({ a: pt(x1, y1, z1), b: pt(x2, y2, z2), cls });

    for (let f = 0; f < nFloors; f++) {
      const z0 = f * H;
      const z1 = z0 + H;
      // floor slab outline
      line(0, 0, z0, ew, 0, z0, "slab");
      line(ew, 0, z0, ew, eh, z0, "slab");
      line(ew, eh, z0, 0, eh, z0, "slab");
      line(0, eh, z0, 0, 0, z0, "slab");
      // walls (extruded centrelines: bottom, top, two verticals)
      for (const w of model.walls) {
        const { p1, p2, wall_type } = w;
        const cls = wall_type === "exterior" ? "wall-ext" : "wall-int";
        line(p1.x, p1.y, z0, p2.x, p2.y, z0, cls);
        line(p1.x, p1.y, z1, p2.x, p2.y, z1, cls);
        line(p1.x, p1.y, z0, p1.x, p1.y, z1, cls);
        line(p2.x, p2.y, z0, p2.x, p2.y, z1, cls);
      }
      // columns as vertical prisms (4 verticals + top square)
      for (const c of model.columns || []) {
        const cx = c.centre?.x ?? c.x, cy = c.centre?.y ?? c.y;
        const hw = (c.width || 0.3) / 2, hd = (c.depth || 0.3) / 2;
        const corners = [[cx - hw, cy - hd], [cx + hw, cy - hd], [cx + hw, cy + hd], [cx - hw, cy + hd]];
        corners.forEach(([px, py]) => line(px, py, z0, px, py, z1, "col"));
        for (let i = 0; i < 4; i++) {
          const [ax, ay] = corners[i], [bx, by] = corners[(i + 1) % 4];
          line(ax, ay, z1, bx, by, z1, "col");
        }
      }
    }

    // bounds → fit into 900×560 with padding
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    segs.forEach((s) => { [s.a, s.b].forEach((p) => { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }); });
    const W = 900, Hpx = 560, pad = 40;
    const scale = Math.min((W - pad * 2) / (maxX - minX || 1), (Hpx - pad * 2) / (maxY - minY || 1));
    const ox = pad - minX * scale + (W - pad * 2 - (maxX - minX) * scale) / 2;
    const oy = pad - minY * scale + (Hpx - pad * 2 - (maxY - minY) * scale) / 2;
    const S = (p) => `${(p.x * scale + ox).toFixed(1)},${(p.y * scale + oy).toFixed(1)}`;
    return { segs, S, W, Hpx };
  }, [model, floors, wallHeight, spin]);

  if (!data) return null;
  const strokeFor = (cls) => ({
    slab: { stroke: "#334155", width: 1 },
    "wall-ext": { stroke: "#00B4D8", width: 1.6 },
    "wall-int": { stroke: "#64748b", width: 1 },
    col: { stroke: "#f59e0b", width: 1.4 },
  }[cls] || { stroke: "#94a3b8", width: 1 });

  // draw slabs first, then internal walls, external walls, columns on top
  const order = ["slab", "wall-int", "wall-ext", "col"];
  const sorted = [...data.segs].sort((a, b) => order.indexOf(a.cls) - order.indexOf(b.cls));

  return (
    <div className={`rounded-2xl border border-border bg-[#0b1120] p-3 ${className}`} data-testid="skeleton-3d">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold text-muted-foreground">3D · هيكل إنشائي بلا أسطح</span>
        <button type="button" data-testid="skeleton-3d-rotate" onClick={() => setSpin((s) => (s + 90) % 360)}
          className="rounded-full border border-border px-3 py-1 text-xs font-bold text-primary transition-colors hover:bg-primary/10">
          تدوير ⟳
        </button>
      </div>
      <svg viewBox={`0 0 ${data.W} ${data.Hpx}`} className="h-auto w-full" style={{ maxHeight: 480 }}>
        {sorted.map((s, i) => {
          const st = strokeFor(s.cls);
          return <line key={i} x1={data.S(s.a).split(",")[0]} y1={data.S(s.a).split(",")[1]} x2={data.S(s.b).split(",")[0]} y2={data.S(s.b).split(",")[1]} stroke={st.stroke} strokeWidth={st.width} strokeLinecap="round" opacity={s.cls === "slab" ? 0.5 : 0.95} />;
        })}
      </svg>
    </div>
  );
};

export default Skeleton3D;
